import React, { useState, useMemo, useEffect } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Bell,
  CalendarClock,
  List,
  SlidersHorizontal,
  MapPin,
} from "lucide-react";
import api from "../../api/api"; // ✅ Import your API instance
import logo from "../../images/SPFLLOGO.png"; // adjust path if needed
import { formatDateDDMMYYYY } from "../../utils/dateFormat";

// =========================
// HELPER FUNCTION
// =========================
const getCalibrationStatus = (dueDateStr) => {
  const today = new Date(); // Use system date
  const dueDate = new Date(dueDateStr);

  if (isNaN(dueDate.getTime())) {
    return {
      text: "Invalid Date",
      key: "compliant",
      days: 9999,
      badgeClass: "bg-gray-100 text-gray-700",
    };
  }

  const timeDiff = dueDate.getTime() - today.getTime();
  const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  if (dayDiff < 0) {
    return {
      text: `Overdue by ${Math.abs(dayDiff)} days`,
      key: "overdue",
      days: dayDiff,
      badgeClass: "bg-red-100 text-red-700",
    };
  }
  if (dayDiff === 0) {
    return { text: "Due Today", key: "dueSoon", days: 0, badgeClass: "bg-yellow-100 text-yellow-700" };
  }
  if (dayDiff <= 30) {
    return {
      text: `Due in ${dayDiff} days`,
      key: "dueSoon",
      days: dayDiff,
      badgeClass: "bg-yellow-100 text-yellow-700",
    };
  }
  return {
    text: `Due in ${dayDiff} days`,
    key: "compliant",
    days: dayDiff,
    badgeClass: "bg-green-100 text-green-700",
  };
};

// =========================
// COMPONENTS
// =========================
const StatCard = ({ title, count, icon, colorClass, onClick, isActive }) => {
  const Icon = icon;
  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-xl flex justify-between items-center cursor-pointer transition-all duration-200 min-h-20 ${colorClass.bg} ${colorClass.text} ${
        isActive ? colorClass.ring : ""
      } hover:-translate-y-1 hover:shadow-lg`}
      aria-pressed={isActive}
    >
      <div>
        <p className="text-sm opacity-80">{title}</p>
        <p className="text-3xl font-semibold mt-1">{count}</p>
      </div>
      <div className="text-2xl">
        <Icon size={28} />
      </div>
    </button>
  );
};

const CalibrationTable = ({ items }) => {
  if (items.length === 0) {
    return <p className="text-center py-8 text-gray-400">No items match this filter.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="p-3 text-left text-xs uppercase tracking-wider text-gray-500 font-bold whitespace-normal break-words align-middle border-b border-gray-200">Status</th>
            <th className="p-3 text-left text-xs uppercase tracking-wider text-gray-500 font-bold whitespace-normal break-words align-middle border-b border-gray-200">Asset Name</th>
            <th className="p-3 text-left text-xs uppercase tracking-wider text-gray-500 font-bold whitespace-normal break-words align-middle border-b border-gray-200">Serial</th>
            <th className="p-3 text-left text-xs uppercase tracking-wider text-gray-500 font-bold whitespace-normal break-words align-middle border-b border-gray-200">Type</th>
            <th className="p-3 text-left text-xs uppercase tracking-wider text-gray-500 font-bold whitespace-normal break-words align-middle border-b border-gray-200">Next Due</th>
            <th className="p-3 text-left text-xs uppercase tracking-wider text-gray-500 font-bold whitespace-normal break-words align-middle border-b border-gray-200">Location</th>
            <th className="p-3 text-left text-xs uppercase tracking-wider text-gray-500 font-bold whitespace-normal break-words align-middle border-b border-gray-200">User</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.assetCalibrationId} className="hover:bg-gray-50">
              <td className="p-3 text-sm border-b border-gray-200">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${item.status.badgeClass}`}>
                  {item.status.text}
                </span>
              </td>
              <td className="p-3 text-sm border-b border-gray-200">{item.assetName}</td>
              <td className="p-3 text-sm border-b border-gray-200">{item.serialNumber}</td>
              <td className="p-3 text-sm border-b border-gray-200">{item.calibrationType}</td>
              <td className="p-3 text-sm border-b border-gray-200">{item.nextDueDate}</td>
              <td className="p-3 text-sm border-b border-gray-200 text-blue-600">{item.currentLocation}</td>
              <td className="p-3 text-sm border-b border-gray-200">{item.responsibleUser || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const NavButton = ({ icon, label, isActive, onClick }) => {
  const Icon = icon;
  return (
    <button 
      onClick={onClick} 
      className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border-none cursor-pointer transition-colors ${
        isActive 
          ? "bg-blue-50 text-blue-600 border border-blue-200" 
          : "bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      }`}
    >
      <Icon size={16} />
      <span>{label}</span>
    </button>
  );
};


 const companyName = sessionStorage.getItem("companyName");
  console.log("companyName from session:", companyName);
const AppNavigation = ({ activeApp, setActiveApp }) => (
  <nav className="bg-white border-b border-gray-200 shadow-sm">
    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-8">
      <div className="flex items-center">
        <img
          src={logo}
          alt="Company Logo"
          className="w-15 h-15 rounded-full bg-white shadow-sm p-1 mr-3"
        />
        <h1 className="text-xl font-bold text-gray-800 m-0 text-left">
          {companyName}
        </h1>
      </div>

      <div className="flex gap-2">
        <NavButton
          icon={SlidersHorizontal}
          label="Calibration Dashboard"
          isActive={activeApp === "calibration"}
          onClick={() => setActiveApp("calibration")}
        />
        {/* <NavButton
          icon={MapPin}
          label="Asset Movement"
          isActive={activeApp === "movement"}
          onClick={() => setActiveApp("movement")}
        /> */}
      </div>
    </div>
  </nav>
);


// =========================
// MAIN DASHBOARD
// =========================
const CalibrationDashboard = () => {
  const [filter, setFilter] = useState("overdue");
  const [apiData, setApiData] = useState([]);

  // ✅ Fetch API data
  // ✅ Fetch API data
useEffect(() => {
  const fetchData = async () => {
    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.CalibrationDashboardAPI(userName);

      console.log("API Response:", response); // 👀 Debug

      // ✅ Your api.js already unwraps json[Object.keys(json)[0]]
      // So `response` = CalibrationDashboardAPIjs
      const tableData = response?.Table || [];

      const formatted = tableData.map((row) => ({
        assetCalibrationId: row.AstCalId,
        assetName: row.AssetName,
        serialNumber: row.serialNo,
        location: row.location,
        responsibleUser: row.responsibility || "-",
        calibrationType: row.ctypename,
        nextDueDate: row.dueDate,
        currentLocation: row.location,
        assetStatus: "Active",
      }));

      setApiData(formatted);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  fetchData();
}, []);



  const allItems = useMemo(
    () =>
      apiData
        .map((item) => ({
          ...item,
          status: getCalibrationStatus(item.nextDueDate),
        }))
        .sort((a, b) => a.status.days - b.status.days),
    [apiData]
  );

  const stats = useMemo(
    () =>
      allItems.reduce(
        (acc, item) => {
          if (item.status.key === "overdue") acc.overdue++;
          if (item.status.key === "dueSoon") acc.dueSoon++;
          if (item.status.key === "compliant") acc.compliant++;
          acc.all++;
          return acc;
        },
        { overdue: 0, dueSoon: 0, compliant: 0, all: 0 }
      ),
    [allItems]
  );

  const filteredItems = useMemo(() => {
    if (filter === "all") return allItems;
    return allItems.filter((item) => item.status.key === filter);
  }, [allItems, filter]);

  const cardColors = {
    overdue: { bg: "bg-red-600", text: "text-white", ring: "ring-2 ring-red-300" },
    dueSoon: { bg: "bg-yellow-500", text: "text-white", ring: "ring-2 ring-yellow-300" },
    compliant: { bg: "bg-green-600", text: "text-white", ring: "ring-2 ring-green-300" },
    all: { bg: "bg-white", text: "text-gray-900", ring: "ring-2 ring-blue-300" },
  };

  const filterTitles = {
    overdue: "Overdue Items",
    dueSoon: "Items Due Soon",
    compliant: "Compliant Items",
    all: "All Items",
  };

  return (
    <div className="max-w-6xl mx-auto my-8 px-6 pl-20">
      <header className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold m-0">Calibration Dashboard</h1>
          <p className="text-gray-500">
            Today is{" "}
            <strong>{formatDateDDMMYYYY(new Date())}</strong>
          </p>
        </div>
        <button 
          onClick={() => setFilter("overdue")} 
          className="flex items-center gap-1.5 bg-blue-600 text-white border-none rounded-md px-3.5 py-2 cursor-pointer hover:bg-blue-700 transition-colors"
        >
          <CalendarClock size={16} /> Quick: Show Overdue
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Overdue"
          count={stats.overdue}
          icon={AlertTriangle}
          colorClass={cardColors.overdue}
          onClick={() => setFilter("overdue")}
          isActive={filter === "overdue"}
        />
        <StatCard
          title="Due in 30 Days"
          count={stats.dueSoon}
          icon={Bell}
          colorClass={cardColors.dueSoon}
          onClick={() => setFilter("dueSoon")}
          isActive={filter === "dueSoon"}
        />
        <StatCard
          title="Compliant"
          count={stats.compliant}
          icon={CheckCircle}
          colorClass={cardColors.compliant}
          onClick={() => setFilter("compliant")}
          isActive={filter === "compliant"}
        />
        <StatCard
          title="All Items"
          count={stats.all}
          icon={List}
          colorClass={cardColors.all}
          onClick={() => setFilter("all")}
          isActive={filter === "all"}
        />
      </div>

      <main className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold m-0">{filterTitles[filter]}</h2>
          <div className="flex items-center gap-2.5">
            <button 
              onClick={() => setFilter("all")} 
              className="bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1 cursor-pointer hover:bg-gray-100 transition-colors"
            >
              Reset Filter
            </button>
            <span className="text-sm text-gray-600">
              Showing <strong>{filteredItems.length}</strong> records
            </span>
          </div>
        </div>
        <CalibrationTable items={filteredItems} />
      </main>
    </div>
  );
};

// =========================
// APP ROOT
// =========================
export default function App() {
  const [activeApp, setActiveApp] = useState("calibration");

  const AssetTracking = () => (
    <div className="max-w-6xl mx-auto my-8 px-6">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h1 className="text-2xl font-bold mb-4">Asset Movement Module</h1>
        <p className="text-gray-600">This is where the AssetTracking component content would be rendered.</p>
      </div>
    </div>
  );

  return (
    <>
      <AppNavigation activeApp={activeApp} setActiveApp={setActiveApp} />
      <main>
        {activeApp === "calibration" && <CalibrationDashboard />}
        {activeApp === "movement" && <AssetTracking />}
      </main>
    </>
  );
}
